import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts/core';
import {
    TooltipComponent,
    GridComponent,
    LegendComponent,
    MarkAreaComponent,
    GraphicComponent,
} from 'echarts/components';
import { LineChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import s from './s.module.less';
import { IAdMetricsTrendDisplay } from 'types/marketing';

// 按需注册ECharts组件
echarts.use([
    TooltipComponent,
    GridComponent,
    LegendComponent,
    LineChart,
    CanvasRenderer,
    MarkAreaComponent,
    GraphicComponent,
]);

interface PerformanceChartProps {
    data?: IAdMetricsTrendDisplay;
    specialty?: string;
    state?: string;
}

const PerformanceChart: React.FC<PerformanceChartProps> = ({ data, specialty, state }) => {
    const chartRef = useRef<HTMLDivElement>(null);
    const chartInstance = useRef<echarts.ECharts | null>(null);

    useEffect(() => {
        if (!chartRef.current || !data) return;

        // 初始化图表
        if (!chartInstance.current) {
            chartInstance.current = echarts.init(chartRef.current);
        }

        let chartDates = data.dates || [];
        let chartBookings = data.bookings || [];
        let chartImpressionShareRate = data.impressionShareRate || [];

        if (chartDates.length === 0) {
            const today = new Date();
            chartDates = Array.from({ length: 7 }, (_, i) => {
                const d = new Date(today);
                d.setDate(d.getDate() - (6 - i));
                return `${d.getMonth() + 1}/${d.getDate()}`;
            });
            chartBookings = new Array(7).fill(0);
            chartImpressionShareRate = new Array(7).fill(0);
        }

        const markAreas =
            data.pauseTimeRanges?.map((period) => [
                {
                    xAxis: period.start,
                    yAxis: 'min',
                },
                { xAxis: period.end, yAxis: 'max' },
            ]) ?? [];

        const option = {
            // 提示框配置
            tooltip: {
                trigger: 'axis',
                backgroundColor: '#fff',
                borderRadius: 8,
                borderWidth: 0,
                padding: 16,
                boxShadow: ' 0 1px 3px 0 rgba(0, 0, 0, 0.10), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                textStyle: {
                    color: '#111827',
                    fontSize: 14,
                    fontWeight: 500,
                },
                extraCssText: 'width: 248px;',
                formatter: (params: any) => {
                    if (!Array.isArray(params) || params.length === 0) return '';

                    const date = params[0].axisValue;
                    let content = `<div style="margin-bottom: 16px; color: #111827;">${date}</div>`;

                    if (specialty && state) {
                        content += `<div style="margin-bottom: 8px; color: #374151; font-size: 14px;">${specialty} in ${state}</div>`;
                    } else if (specialty) {
                        content += `<div style="margin-bottom: 8px; color: #374151; font-size: 14px;">${specialty}</div>`;
                    } else if (state) {
                        content += `<div style="margin-bottom: 8px; color: #374151; font-size: 14px;">${state}</div>`;
                    }

                    params.forEach((param: any) => {
                        const value = param.seriesName === 'Impression share (%)' ? `${param.value}%` : param.value;

                        const label = param.seriesName === 'Impression share (%)' ? 'Impression share' : 'Booking';

                        content += `
                            <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px;">
                                <span style="color: #15191E; font-weight: 500;">${label}:</span>
                                <span style="font-weight: 500; color: #4B5563; font-size: 14px;">${value}</span>
                            </div>
                        `;
                    });

                    return content;
                },
            },
            // 图例配置
            legend: {
                data: [
                    {
                        name: 'Booking',
                        icon: 'rect',
                        itemStyle: {
                            color: '#00816B',
                        },
                    },
                    {
                        name: 'Impression share (%)',
                        icon: 'rect',
                        itemStyle: {
                            color: '#FDB284',
                        },
                    },
                ],
                top: 'top',
                left: 'left',
                textStyle: {
                    color: '#303030',
                    fontSize: 13,
                    fontWeight: 500,
                },
                itemWidth: 16,
                itemHeight: 2,
                itemGap: 16,
                icon: 'rect',
            },
            // 网格配置
            grid: {
                top: 40,
                left: 40,
                right: 40,
                bottom: 60,
                containLabel: false,
            },
            // X轴配置
            xAxis: {
                type: 'category',
                data: chartDates,
                axisLine: {
                    show: false,
                },
                axisTick: {
                    show: false,
                },
                axisLabel: {
                    color: '#6B7280',
                    fontSize: 14,
                    fontWeight: 400,
                    margin: 12,
                },
                splitLine: {
                    show: false,
                },
            },
            // Y轴配置
            yAxis: [
                {
                    type: 'value',
                    position: 'right',
                    axisLine: {
                        show: false,
                    },
                    axisTick: {
                        show: false,
                    },
                    axisLabel: {
                        color: '#9CA3AF',
                        fontSize: 12,
                        fontWeight: 400,
                        margin: 12,
                    },
                    splitLine: {
                        lineStyle: {
                            color: '#F3F4F6',
                            width: 1,
                            type: 'solid',
                        },
                    },
                    min: 0,
                    max: () => {
                        // 动态设置Y轴最大值
                        const maxBooking = Math.max(0, ...chartBookings);
                        return maxBooking || 10;
                    },
                },
                {
                    type: 'value',
                    position: 'left',
                    axisLine: {
                        show: false,
                    },
                    axisTick: {
                        show: false,
                    },
                    axisLabel: {
                        color: '#9CA3AF',
                        fontSize: 12,
                        fontWeight: 400,
                        margin: 12,
                        formatter: '{value}%',
                    },
                    splitLine: {
                        show: false,
                    },
                    min: 0,
                    max: () => {
                        // 动态设置Y轴最大值
                        const maxShare = Math.max(0, ...chartImpressionShareRate);
                        return Math.ceil(maxShare / 10) * 10 || 1;
                    },
                },
            ],
            // 数据系列配置
            series: [
                {
                    name: 'Booking',
                    type: 'line',
                    yAxisIndex: 0,
                    data: chartBookings,
                    lineStyle: {
                        color: '#00816B',
                        width: 4,
                    },
                    itemStyle: {
                        color: '#00816B',
                        borderWidth: 0,
                    },
                    symbol: 'circle',
                    symbolSize: 4,
                    smooth: true,
                    ...(markAreas.length > 0 && {
                        markArea: {
                            silent: true,
                            itemStyle: {
                                color: 'rgba(107, 114, 128, 0.10)',
                            },
                            data: markAreas,
                            z: 100,
                        },
                    }),
                },
                {
                    name: 'Impression share (%)',
                    type: 'line',
                    yAxisIndex: 1,
                    data: chartImpressionShareRate,
                    lineStyle: {
                        color: '#FDB284',
                        width: 4,
                    },
                    itemStyle: {
                        color: '#FDB284',
                        borderWidth: 0,
                    },
                    symbol: 'circle',
                    symbolSize: 4,
                    smooth: true,
                },
            ],
        };

        chartInstance.current.setOption(option, true);

        function renderPauseLabels() {
            if (!data || !data.dates?.length || !data.pauseTimeRanges?.length) return;

            // 获取图表容器的尺寸
            const chartDom = chartInstance.current?.getDom();
            const chartWidth = chartDom?.clientWidth ?? 0;
            const chartHeight = chartDom?.clientHeight ?? 0;

            const graphics = data.pauseTimeRanges?.map((p) => {
                const xVal = data.dates?.findIndex((date) => date === p.start) ?? 0;
                const yVal = data.bookings?.[xVal] ?? 0; // 起点对应的 y 值（没有就置 0）
                const [px, py] = chartInstance?.current?.convertToPixel({ xAxisIndex: 0, yAxisIndex: 0 }, [
                    xVal,
                    yVal,
                ]) ?? [0, 0];

                const textContent =
                    p.start !== p.end
                        ? `Pro-Listings campaign was paused on \n ${p.start} ~ ${p.end}`
                        : `Pro-Listings campaign was paused on \n ${p.start}`; // 手动换行

                // 标签的尺寸
                const labelWidth = 260;
                const labelHeight = 58;

                // 动态调整标签位置，防止溢出
                let adjustedLeft = px;
                let adjustedTop = py;

                // 水平位置调整：如果标签会超出右边界，则向左偏移
                if (px + labelWidth > chartWidth - 20) {
                    // 留20px边距
                    adjustedLeft = chartWidth - labelWidth - 20;
                }
                // 如果调整后会超出左边界，则贴左边
                if (adjustedLeft < 20) {
                    adjustedLeft = 20;
                }

                // 垂直位置调整：如果标签会超出底部边界，则向上偏移
                if (py + labelHeight > chartHeight - 20) {
                    // 留20px边距
                    adjustedTop = py - labelHeight - 10; // 在数据点上方显示
                }
                // 如果调整后会超出顶部边界，则贴顶部
                if (adjustedTop < 20) {
                    adjustedTop = 20;
                }

                return {
                    type: 'group',
                    left: adjustedLeft,
                    top: adjustedTop,
                    z: 100,
                    children: [
                        {
                            type: 'rect',
                            shape: { width: labelWidth, height: labelHeight, r: 8, padding: 16 },
                            z: 100,
                            style: {
                                fill: '#fff',
                                lineWidth: 1,
                                shadowBlur: 5,
                                shadowColor: 'rgba(0,0,0,0.1)',
                            },
                        },
                        {
                            type: 'text',
                            left: 10,
                            top: 10,
                            z: 100,
                            style: {
                                text: textContent,
                                font: '16px Inter',
                                fill: '#15191E',
                                lineHeight: 22,
                                align: 'left',
                                fontWeight: 500,
                            },
                        },
                    ],
                };
            });

            chartInstance.current?.setOption(
                { graphic: graphics },
                { replaceMerge: ['graphic'] }, // 防止重复叠加
            );
        }
        renderPauseLabels();

        // 处理窗口大小变化
        const handleResize = () => {
            chartInstance.current?.resize();
            renderPauseLabels();
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [data, specialty, state]);

    // 清理图表实例
    useEffect(() => {
        return () => {
            if (chartInstance.current) {
                chartInstance.current.dispose();
                chartInstance.current = null;
            }
        };
    }, []);

    return (
        <div className={s.container}>
            <div ref={chartRef} className={s.chart} />
        </div>
    );
};

export default PerformanceChart;
